import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Upload, FileImage, Loader2 } from "lucide-react";
import { usePaymentCategories } from "@/hooks/payments/usePaymentCategories";
import { useCreateReceipt } from "@/hooks/payments/usePaymentReceipts";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";

interface UploadReceiptDialogProps {
  userId: string;
  gymId: string;
}

export const UploadReceiptDialog = ({ userId, gymId }: UploadReceiptDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { error: showError } = useNotifications();

  const { categories, isLoading: categoriesLoading } = usePaymentCategories(gymId);
  const createReceipt = useCreateReceipt();

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      if (selected.size > 5 * 1024 * 1024) {
        showError("El archivo no puede superar los 5MB");
        return;
      }
      setFile(selected);
    }
  };

  const handleSubmit = async () => {
    if (!file || !selectedCategoryId) return;

    setIsUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${gymId}/${userId}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("payment-receipts")
        .upload(path, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("payment-receipts")
        .getPublicUrl(path);

      await createReceipt.mutateAsync({
        user_id: userId,
        gym_id: gymId,
        category_id: selectedCategoryId,
        category_name: selectedCategory?.name || null,
        receipt_url: urlData.publicUrl,
      });

      setIsOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error uploading receipt:", error);
      showError("No se pudo subir el comprobante");
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedCategoryId("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
      <DialogTrigger asChild>
        <Button className="w-full gap-2">
          <Upload className="h-4 w-4" />
          Subir comprobante de pago
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95%] max-w-md mx-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Subir comprobante de pago</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Categoría de pago</label>
            {categoriesLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
              </div>
            ) : categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay categorías disponibles</p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategoryId(cat.id)}
                    className={`text-left p-3 rounded-lg border transition-colors ${
                      selectedCategoryId === cat.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <span className="font-medium text-sm">{cat.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ${cat.amount} · {cat.validity_days} días
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Comprobante (imagen o PDF)</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileImage className="h-5 w-5 text-primary" />
                  <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                </div>
              ) : (
                <div className="space-y-1">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Click para seleccionar archivo</p>
                  <p className="text-xs text-muted-foreground">Máximo 5MB</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!selectedCategoryId || !file || isUploading}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              "Enviar comprobante"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
